import React, {
  forwardRef,
  PropsWithChildren,
  useMemo,
  useLayoutEffect,
  useRef,
  useState,
  useEffect
} from 'react'
import cls from 'classnames'
import {observer} from 'mobx-react'
import {useBasketStore, useGlobalStore} from '../../../../store/hooks/useStore'
import {Box, Button, Flex, Text} from '../../../../vanilla'
import {CircleStarGradientIcon, CircleStarIcon} from '../../../../components/icons'
import {DeliverySlot} from '../../../../types/store/basket'
import {useVipRestrictions} from '../../../../hooks/use-vip-restrictions'
import * as styles from '../../styles.css'

const SameDayToolTip = () => {
  return (
    <Box className={styles.sameDayToolTipContainer} zIndex="docked">
      <Box className={styles.sameDayToolTipBody}>
        <CircleStarIcon className={styles.sameDayToolTipStar} />
        <Text
          variant="unstyled"
          fontWeight="bold"
          fontSize="xs"
          textAlign="center"
          style={{padding: '5px 7px', lineHeight: '15px'}}
        >
          Same Day Now Available
        </Text>
      </Box>
    </Box>
  )
}

const VipToolTip = () => {
  return (
    <Box className={styles.vipToolTipContainer}>
      <Box className={styles.vipToolTipBody}>
        <CircleStarGradientIcon className={styles.vipToolTipStar} />
        <Text fontSize="xxs" fontWeight="bold" textAlign="center" variant="unstyled" color="white">
          VIP Access
        </Text>
      </Box>
    </Box>
  )
}

interface TabProps extends DeliverySlot {
  isSelected: boolean
  onClick: () => void
  isVip: boolean
}

const Tab = forwardRef<HTMLDivElement, PropsWithChildren<TabProps>>(
  ({dayText, day, month, isSelected, isToday, hasAvailableSlots, onClick, isVip}, ref) => {
    return (
      <Box
        ref={ref}
        className={cls('calendar-tab', {
          'calendar-tab-vip': isVip,
        })}
        cursor="pointer"
        onClick={onClick}
        style={{
          borderBottom: '3px solid',
          ...(!isVip && {
            borderBottomColor: isSelected ? '#fff' : '#f3f3f3',
          }),
          ...(isVip && {
            borderBottomColor: '#fff',
          }),
          ...(isSelected && {
            borderLeft: '1px solid #af0937',
            borderRight: '1px solid #af0937',
          }),
        }}
        position="relative"
        data-calendar-tab-id = {day + month}
      >
        {isToday && hasAvailableSlots && <SameDayToolTip />}
        {isVip && hasAvailableSlots && !isToday && <VipToolTip />}
        <Box
          className="calendar-tab-header"
          data-test-selector="calendar-tab-header"
          style={
            isVip
              ? {
                  backgroundColor: '#ac8d4b',
                  padding: '7px 7px 68px',
                  marginBottom: '-65px',
                  clipPath: 'polygon(50% 44%, 100% 38%, 100% 0px, 0px 0px, 0% 38%)',
                  backgroundImage:
                    'linear-gradient(90deg, rgb(172, 141, 75) 0%, rgb(255, 238, 202) 33%, rgb(255, 238, 202) 66%, rgb(172, 141, 75) 100%)',
                }
              : {
                  backgroundColor:
                    isToday && !isSelected ? '#f3a124' : isSelected ? '#af0937' : '#fcd5cc',
                  padding: '7px',
                }
          }
        >
          <Text
            variant="unstyled"
            data-test-selector="calendar-day-text"
            fontSize="xs"
            fontWeight="bold"
            textAlign="center"
            color={isSelected && !isVip ? 'white' : 'inherit'}
          >
            {dayText}
          </Text>
        </Box>
        <Box
          paddingY="20px"
          className="calendar-tab-body"
          backgroundColor="white"
          style={{
            ...(isVip && {
              paddingTop: '24px',
            }),
          }}
        >
          <Text textAlign="center" variant="text6" data-test-selector="calendar-day">
            {day}
          </Text>
          <Text textAlign="center" variant="text2" lineHeight="none" data-test-selector="calendar-month">
            {month}
          </Text>
        </Box>
      </Box>
    )
  },
)

type TabObj = {
  ref: HTMLDivElement
  isVip: boolean
}

const useOffsetTabs = () => {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : Infinity
  const refsArr = useRef<TabObj[]>([])
  const [tabsRefs, setTabsRefs] = React.useState<TabObj[]>([])
  const [areVipTabsOffScreen, setAreVipTabsOffScreen] = React.useState(false)

  const addTabRef = (ref: TabObj) => {
    refsArr.current = [...refsArr.current, ref]
    setTabsRefs(refsArr.current)
  }

  useLayoutEffect(() => {
    for (const tab of tabsRefs) {
      const rect = tab.ref?.getBoundingClientRect?.()

      if (rect?.x > viewportWidth && tab.isVip) {
        setAreVipTabsOffScreen(true)
        break
      }
    }
  }, [tabsRefs])

  return {
    addTabRef,
    tabsRefs,
    areVipTabsOffScreen,
  }
}

export const CalendarTabs = observer(
  ({
    selectedTab,
    setSelectedTab,
  }: {
    selectedTab: number | undefined
    setSelectedTab: (tabIndex: number) => void
  }) => {
    const {deliverySlotArray: slots, sameDayDeliveryAvailable} = useBasketStore()
    const {isVipDeliverySlot} = useVipRestrictions()
    const {addTabRef, areVipTabsOffScreen} = useOffsetTabs()
    const container = useRef<HTMLDivElement | null>(null)
    const {
      getCustomSitePreferenceById,
    } = useGlobalStore()
    const [deliveryShowSameDayFirst, setDeliveryShowSameDayFirst] = useState(false);
    const initialLoadRef = useRef(true)
    const scrollContainer = () => {
      if (container.current) container.current.scrollLeft = 10000
    }
    useEffect(() => {
      (async function () {
        try {
          const value = await getCustomSitePreferenceById('deliveryShowSameDayFirst')
          setDeliveryShowSameDayFirst(value as unknown as boolean)
        } catch (error) {
          console.error("Error fetching preference:", error)
        }
      })()
    }, [])
    
    // Checks custom pref and if there are slots available for same day delivery
    useEffect(() => {
      if (deliveryShowSameDayFirst && initialLoadRef.current && sameDayDeliveryAvailable) {
        setSelectedTab(0)
        initialLoadRef.current = false
      }
    }, [deliveryShowSameDayFirst, setSelectedTab])

    /** Memoize the results of the tabs tied to the slot data */
    const tabMarkup = useMemo(() => {
      const mappedSlots = slots?.map((slot, idx) => {
        const ref = (node: HTMLDivElement) => {
          if (node) {
            addTabRef({ref: node, isVip})
          }
        }

        const isVip = isVipDeliverySlot(slot)

        return (
          <Tab
            key={`tab-${idx}`}
            {...slot}
            isVip={isVip}
            onClick={() => setSelectedTab(idx)}
            isSelected={typeof selectedTab === 'number' ? selectedTab === idx : idx === 1}
            ref={ref}
          />
        )
      })

      return mappedSlots
    }, [slots, selectedTab])

    return (
      <>
        {areVipTabsOffScreen ? (
          <Button onClick={scrollContainer} className={styles.vipSlotMobileButton}>
            <Box display="grid" style={{gridAutoFlow: 'column'}} gap="4px" alignItems="center">
              <CircleStarIcon className={styles.vipToolTipStar} />
              <Text>VIP Delivery Slots</Text>
            </Box>
          </Button>
        ) : null}
        <Flex
          style={{gap: '3px'}}
          paddingTop={sameDayDeliveryAvailable ? '72px' : '44px'}
          overflowX="auto"
          ref={container}
        >
          {tabMarkup}
        </Flex>
      </>
    )
  },
)
